/**
 * Insertion de dernière minute à une intersection de la grille.
 *
 * L'antenne est pleine : poser un spot ou un coming next entre deux éléments
 * collés suppose que quelqu'un cède du temps. C'est toujours l'infoservice, et
 * toujours par le bord qui touche l'insertion — celui que le nouvel élément
 * vient occuper aussitôt. Ce module exécute la manœuvre, puis va chercher les copies
 * de la séquence ailleurs dans la semaine : une soirée rediffusée la nuit doit
 * recevoir le même spot, sans quoi les deux passages divergent en silence.
 */

import type { ScheduleItem } from '../../types/index.js'
import { itemsApi } from '../../lib/api.js'
import { adBlocksApi, infoservicesApi, type AdBlockInsertionSpace } from '../../lib/adsApi.js'
import { AD_BLOCK_BLACKSCREEN_SEC } from '../../lib/adBlockTiming.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import {
  infoserviceInsertPlansAt, infoserviceRuns, itemIdentityKey,
  type InfoserviceInsertPlan,
} from './infoserviceResize.js'

/** Ce qu'il faut recréer à l'identique dans chaque copie de la séquence. */
export type InsertionRecipe =
  | {
      kind: 'ITEM'
      itemType: 'PROGRAM' | 'JINGLE'
      programId: string
      mediaAssetId?: string
      durationSec: number
      label: string
    }
  | {
      kind: 'AD_BLOCK'
      spots: Array<{ spotId: string; durationSec: number }>
      durationSec: number
      label: string
    }
  | {
      kind: 'INFOSERVICE'
      durationSec: number
      label: string
    }

/** Instant de début de l'élément créé dans le trou que le plan va ouvrir. */
export function insertionStartMs(plan: InfoserviceInsertPlan, durationSec: number): number {
  return plan.edge === 'START'
    ? plan.boundaryMs
    : plan.boundaryMs - durationSec * 1_000
}

/** Sérialise le temps libéré et les décalages pour l'écriture atomique API. */
export function insertionSpaceRequest(
  plan: InfoserviceInsertPlan,
  durationSec: number,
): AdBlockInsertionSpace {
  const durationMs = durationSec * 1_000
  const towardsFuture = plan.edge === 'START'
  return {
    infoservice: {
      itemId: plan.trim.itemId,
      startTime: towardsFuture
        ? new Date(new Date(plan.trim.startTime).getTime() + durationMs).toISOString()
        : plan.trim.startTime,
      endTime: towardsFuture
        ? plan.trim.endTime
        : new Date(new Date(plan.trim.endTime).getTime() - durationMs).toISOString(),
      memberIds: plan.trim.memberIds,
    },
    shifts: plan.shift.map(shifted => ({
      id: shifted.id,
      expectedStartTime: new Date(shifted.startMs).toISOString(),
      startTime: new Date(shifted.startMs + (towardsFuture ? durationMs : -durationMs)).toISOString(),
    })),
  }
}

/**
 * Applique le plan puis crée l'élément.
 *
 * L'ordre est la seule chose qui compte ici : la plage se rétracte d'abord, ce
 * qui ouvre un trou ; la chaîne l'occupe ensuite un élément à la fois, chacun se
 * déplaçant dans la place que le précédent vient de libérer. Déplacer avant de
 * raccourcir ferait refuser chaque mouvement pour cause de chevauchement.
 *
 * Le sens de parcours suit celui du trou. Plage en amont : sa fin et la chaîne
 * se déplacent plus tôt, du plus ancien au plus récent. Plage en aval : son
 * début et la chaîne se déplacent plus tard, du plus récent au plus ancien —
 * c'est celui qui touche la plage qui bouge en premier.
 */
export async function openInsertionSpace(
  plan: InfoserviceInsertPlan,
  durationSec: number,
): Promise<void> {
  const towardsFuture = plan.edge === 'START'
  const request = insertionSpaceRequest(plan, durationSec)

  await itemsApi.resizeInfoservice(
    request.infoservice.itemId,
    request.infoservice.startTime,
    request.infoservice.endTime,
    request.infoservice.memberIds,
  )

  const order = towardsFuture ? [...plan.shift].reverse() : plan.shift
  for (const shifted of order) {
    const target = request.shifts.find(entry => entry.id === shifted.id)!
    await itemsApi.move(shifted.id, target.startTime)
  }
}

/** Ouvre la place, puis y pose le contenu décrit par la recette. */
export async function applyInsertion(
  channelId: string,
  gridId: string,
  plan: InfoserviceInsertPlan,
  recipe: InsertionRecipe,
): Promise<void> {
  const durationMs = recipe.durationSec * 1_000
  const startMs = insertionStartMs(plan, recipe.durationSec)
  if (recipe.kind === 'AD_BLOCK') {
    let cursor = startMs
    const spots = recipe.spots.map(spot => {
      const row = {
        spotId: spot.spotId,
        startTime: new Date(cursor).toISOString(),
        durationSec: spot.durationSec,
      }
      cursor += (spot.durationSec + AD_BLOCK_BLACKSCREEN_SEC) * 1_000
      return row
    })
    await adBlocksApi.applyWithInsertion({
      channelId,
      gridId,
      spots,
      insertion: insertionSpaceRequest(plan, recipe.durationSec),
    })
    return
  }

  if (recipe.kind === 'INFOSERVICE') {
    await infoservicesApi.resolve({ channelId })
    await openInsertionSpace(plan, recipe.durationSec)
    const startTime = new Date(startMs).toISOString()
    await infoservicesApi.fillGaps({
      channelId,
      gridId,
      date: broadcastDayKey(new Date(startMs)),
      from: startTime,
      to: new Date(startMs + durationMs).toISOString(),
    })
    return
  }

  await openInsertionSpace(plan, recipe.durationSec)

  await itemsApi.create({
    gridId,
    startTime: new Date(startMs).toISOString(),
    endTime: new Date(startMs + durationMs).toISOString(),
    durationSec: recipe.durationSec,
    itemType: recipe.itemType,
    sortOrder: Math.floor(startMs / 1_000),
    programId: recipe.programId,
    ...(recipe.mediaAssetId ? { mediaAssetId: recipe.mediaAssetId } : {}),
  })
}

/** Copie de la séquence modifiée, prête à recevoir la même insertion. */
export interface DuplicateInsertion {
  /** Intersection équivalente dans la copie. */
  boundaryMs: number
  /** Écart avec la séquence d'origine, positif quand la copie passe après. */
  offsetMs: number
  plan: InfoserviceInsertPlan
  /** Grille de la copie : une rediffusion de nuit peut tomber sur la suivante. */
  gridId: string
  /** Assez de marge sur l'infoservice de la copie pour loger l'insertion. */
  fits: boolean
}

export interface OccupiedInsertionBoundary {
  occupied: boolean
  plans: InfoserviceInsertPlan[]
}

/**
 * Détecte une intersection réellement occupée et les manœuvres qui peuvent y
 * ouvrir de la place. Le formulaire global « Ajouter » utilise ce calcul après
 * la saisie de l'heure : il ne doit jamais poser un jingle par-dessus le
 * programme qui commence déjà à cet instant.
 */
export function occupiedInsertionBoundaryAt(
  items: ScheduleItem[],
  boundaryMs: number,
  toleranceMs = 10_000,
): OccupiedInsertionBoundary {
  const live = items.filter(item => item.status !== 'SKIPPED')
  const hasItemBefore = live.some(item => {
    const endMs = new Date(item.endTime).getTime()
    return endMs <= boundaryMs && boundaryMs - endMs <= toleranceMs
  })
  const hasItemAfter = live.some(item => {
    const startMs = new Date(item.startTime).getTime()
    return startMs >= boundaryMs && startMs - boundaryMs <= toleranceMs
  })
  if (!hasItemBefore || !hasItemAfter) return { occupied: false, plans: [] }

  return {
    occupied: true,
    plans: infoserviceInsertPlansAt(items, infoserviceRuns(items), boundaryMs, toleranceMs),
  }
}

/**
 * En deçà, c'est la séquence d'origine qu'on regarde, pas une copie. Deux
 * diffusions d'une même soirée ne sont jamais espacées de moins d'une heure.
 */
const MIN_DUPLICATE_OFFSET_MS = 3_600_000

/**
 * Repère les intersections jumelles dans la fenêtre fournie.
 *
 * Le critère est le voisinage : même contenu avant, même contenu après. C'est
 * ce que l'opérateur reconnaît à l'œil quand il dit « c'est la même séquence »,
 * et c'est assez strict pour ne pas confondre deux jingles de même habillage
 * posés dans des soirées différentes.
 */
export function findDuplicateInsertions(
  weekItems: ScheduleItem[],
  plan: InfoserviceInsertPlan,
  recipe: InsertionRecipe,
  toleranceMs = 10_000,
): DuplicateInsertion[] {
  if (!plan.signature.after) return []

  const runs = infoserviceRuns(weekItems)
  const live = weekItems.filter(item => item.status !== 'SKIPPED')
  const found: DuplicateInsertion[] = []
  const seen = new Set<number>()

  for (const candidate of live) {
    if (itemIdentityKey(candidate) !== plan.signature.after) continue

    const boundaryMs = new Date(candidate.startTime).getTime()
    const offsetMs = boundaryMs - plan.boundaryMs
    if (Math.abs(offsetMs) < MIN_DUPLICATE_OFFSET_MS) continue
    if (seen.has(boundaryMs)) continue

    // Même voisin avant : sans lui, deux séquences qui partagent seulement leur
    // programme d'ouverture passeraient pour des copies l'une de l'autre.
    if (plan.signature.before) {
      const previous = live.find(item => (
        Math.abs(new Date(item.endTime).getTime() - boundaryMs) <= toleranceMs
      ))
      if (!previous || itemIdentityKey(previous) !== plan.signature.before) continue
    }

    // Une insertion entre deux programmes peut choisir la réserve du haut ou
    // du bas. La copie doit reprendre le même côté, pas retomber sur la
    // priorité amont historique.
    const candidatePlan = infoserviceInsertPlansAt(weekItems, runs, boundaryMs, toleranceMs)
      .find(candidate => candidate.edge === plan.edge)
    if (!candidatePlan) continue
    if (sequenceAlreadyContainsRecipe(live, boundaryMs, recipe, toleranceMs)) continue

    seen.add(boundaryMs)
    found.push({
      boundaryMs,
      offsetMs,
      plan: candidatePlan,
      gridId: candidate.gridId,
      fits: candidatePlan.slackMs >= recipe.durationSec * 1_000,
    })
  }

  return found.sort((left, right) => left.boundaryMs - right.boundaryMs)
}

/**
 * Une frontière technique ne suffit pas à elle seule à décrire tout le
 * raccord éditorial. Avant de proposer une répercussion, on regarde donc
 * jusqu'aux programmes qui l'encadrent : si le même jingle existe déjà quelque
 * part entre eux, en ajouter un second ferait un doublon même si un court
 * infoservice les sépare.
 */
function sequenceAlreadyContainsRecipe(
  items: ScheduleItem[],
  boundaryMs: number,
  recipe: InsertionRecipe,
  toleranceMs: number,
): boolean {
  if (recipe.kind !== 'ITEM' || recipe.itemType !== 'JINGLE') return false

  const anchors = items.filter(isProgramAnchor)
  const previous = anchors
    .filter(item => new Date(item.endTime).getTime() <= boundaryMs + toleranceMs)
    .sort((left, right) => new Date(right.endTime).getTime() - new Date(left.endTime).getTime())[0]
  const next = anchors
    .filter(item => new Date(item.startTime).getTime() >= boundaryMs - toleranceMs)
    .sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime())[0]

  const sequenceStart = previous ? new Date(previous.endTime).getTime() : boundaryMs
  const sequenceEnd = next ? new Date(next.startTime).getTime() : boundaryMs
  return items.some(item => (
    item.itemType === 'JINGLE'
    && item.programId === recipe.programId
    && new Date(item.startTime).getTime() >= sequenceStart - toleranceMs
    && new Date(item.endTime).getTime() <= sequenceEnd + toleranceMs
  ))
}

function endingAt(items: ScheduleItem[], boundaryMs: number, toleranceMs: number): ScheduleItem | null {
  let best: ScheduleItem | null = null
  for (const item of items) {
    const endMs = new Date(item.endTime).getTime()
    if (endMs > boundaryMs) continue
    if (Math.abs(endMs - boundaryMs) > toleranceMs) continue
    if (!best || endMs > new Date(best.endTime).getTime()) best = item
  }
  return best
}

function edgeRank(edge: InfoserviceInsertPlan['edge'], preferredEdges: InfoserviceInsertPlan['edge'][]): number {
  const index = preferredEdges.indexOf(edge)
  return index >= 0 ? index : preferredEdges.length
}

export interface ProgramPairInsertionSource {
  afterProgram: ScheduleItem
  beforeProgram: ScheduleItem
  sourceBoundaryMs: number
  preferredEdges: InfoserviceInsertPlan['edge'][]
}

/**
 * Recherche après coup les mêmes deux programmes directement collés ailleurs.
 *
 * Contrairement à `findDuplicateInsertions`, la signature ne vient pas des
 * voisins immédiats d'une intersection avant insertion : le jingle est déjà
 * posé, donc on reconstruit volontairement la paire éditoriale qui l'encadre.
 */
export function findProgramPairDuplicateInsertions(
  weekItems: ScheduleItem[],
  source: ProgramPairInsertionSource,
  durationSec: number,
  toleranceMs = 10_000,
): DuplicateInsertion[] {
  const beforeKey = itemIdentityKey(source.afterProgram)
  const afterKey = itemIdentityKey(source.beforeProgram)
  const runs = infoserviceRuns(weekItems)
  const live = weekItems.filter(item => item.status !== 'SKIPPED')
  const found: DuplicateInsertion[] = []
  const seen = new Set<number>()

  for (const candidate of live) {
    if (itemIdentityKey(candidate) !== afterKey) continue

    const boundaryMs = new Date(candidate.startTime).getTime()
    const offsetMs = boundaryMs - source.sourceBoundaryMs
    if (Math.abs(offsetMs) < MIN_DUPLICATE_OFFSET_MS) continue
    if (seen.has(boundaryMs)) continue

    const previous = endingAt(live, boundaryMs, toleranceMs)
    if (!previous || itemIdentityKey(previous) !== beforeKey) continue

    const plans = infoserviceInsertPlansAt(weekItems, runs, boundaryMs, toleranceMs)
      .sort((left, right) => (
        edgeRank(left.edge, source.preferredEdges) - edgeRank(right.edge, source.preferredEdges)
        || right.slackMs - left.slackMs
      ))
    const plan = plans[0]
    if (!plan) continue

    seen.add(boundaryMs)
    found.push({
      boundaryMs,
      offsetMs,
      plan,
      gridId: candidate.gridId,
      fits: plan.slackMs >= durationSec * 1_000,
    })
  }

  return found.sort((left, right) => left.boundaryMs - right.boundaryMs)
}

const isProgramAnchor = (item: ScheduleItem) => item.itemType === 'PROGRAM' || item.itemType === 'LIVE'

export function programPairAroundInsertedTechnicalItem(
  weekItems: ScheduleItem[],
  item: ScheduleItem,
  toleranceMs = 10_000,
): ProgramPairInsertionSource | null {
  if (!['JINGLE', 'INFOSERVICE'].includes(item.itemType)) return null
  const live = weekItems
    .filter(item => item.status !== 'SKIPPED')
    .sort((left, right) => (
      new Date(left.startTime).getTime() - new Date(right.startTime).getTime()
      || left.sortOrder - right.sortOrder
    ))
  const sourceIndex = live.findIndex(candidate => candidate.id === item.id)
  if (sourceIndex < 0) return null

  const afterProgram = [...live.slice(0, sourceIndex)].reverse().find(isProgramAnchor)
  const beforeProgram = live.slice(sourceIndex + 1).find(isProgramAnchor)
  if (!afterProgram || !beforeProgram) return null

  const itemStartMs = new Date(item.startTime).getTime()
  const itemEndMs = new Date(item.endTime).getTime()
  const preferredEdges: InfoserviceInsertPlan['edge'][] = []
  if (Math.abs(new Date(afterProgram.endTime).getTime() - itemStartMs) <= toleranceMs) preferredEdges.push('START')
  if (Math.abs(new Date(beforeProgram.startTime).getTime() - itemEndMs) <= toleranceMs) preferredEdges.push('END')
  if (preferredEdges.length === 0) preferredEdges.push('END', 'START')

  return {
    afterProgram,
    beforeProgram,
    sourceBoundaryMs: new Date(beforeProgram.startTime).getTime(),
    preferredEdges,
  }
}
